<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import SavedViewIcon from '@/components/savedViews/SavedViewIcon.vue';
import { useUpdateSavedViewAppearance } from '@/composables/api/useSavedViewsApi';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';
import {
    DEFAULT_SAVED_VIEW_COLOR,
    DEFAULT_SAVED_VIEW_ICON,
    SAVED_VIEW_COLORS,
    SAVED_VIEW_ICONS,
    savedViewTintFor,
} from '@/utils/savedViewAppearance';
import type { SavedViewRecord } from '@/utils/savedViews';

/**
 * Pick a saved view's icon and colour, the way Linear does for its views: a row
 * of colours, then a grid of line-art icons drawn in the chosen colour. Each
 * click saves at once, so there is no Save button, only Done.
 */
const props = defineProps<{ view: SavedViewRecord }>();
const open = defineModel<boolean>('open', { required: true });

// In the page only while open or fading out, so a closed picker leaves no Teleport behind.
const { mounted: dialogMounted, onCloseFinished: onDialogClosed } = useMountedWhileOpen(open);

const appearanceMutation = useUpdateSavedViewAppearance();
const search = ref('');

// What the picker shows as chosen. It follows the view, which the mutation
// patches before the server answers, so a click shows at once.
const chosenIcon = computed(() => props.view.icon ?? DEFAULT_SAVED_VIEW_ICON);
const chosenColor = computed(() => props.view.color ?? DEFAULT_SAVED_VIEW_COLOR);
const tint = computed(() => savedViewTintFor(props.view));
const isDefault = computed(() => (props.view.icon ?? null) === null && (props.view.color ?? null) === null);

const shownIcons = computed(() => {
    const term = search.value.trim().toLowerCase();
    if (term === '') return SAVED_VIEW_ICONS;
    return SAVED_VIEW_ICONS.filter((option) => option.label.toLowerCase().includes(term) || option.key.includes(term));
});

watch(open, (isOpen) => {
    if (isOpen) search.value = '';
});

function save(icon: string | null, color: string | null) {
    appearanceMutation.mutate({ entity: props.view.entity, savedViewId: props.view.id, icon, color });
}

function pickColor(color: string) {
    save(props.view.icon ?? null, color);
}

function pickIcon(icon: string) {
    save(icon, props.view.color ?? null);
}
</script>

<template>
  <Dialog v-if="dialogMounted" v-model:open="open">
    <DialogContent class="sm:max-w-sm" data-testid="saved-view-appearance-picker" @close-auto-focus="onDialogClosed">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <SavedViewIcon :appearance="view" class="size-5" />
          <span class="truncate">{{ view.name }}</span>
        </DialogTitle>
        <DialogDescription>Pick an icon and a colour. Each change saves straight away.</DialogDescription>
      </DialogHeader>

      <div role="radiogroup" aria-label="Colour" class="flex flex-wrap gap-1.5">
        <button
          v-for="option in SAVED_VIEW_COLORS"
          :key="option.key"
          type="button"
          role="radio"
          :aria-checked="chosenColor === option.key"
          :aria-label="option.label"
          :title="option.label"
          :data-testid="`saved-view-color-${option.key}`"
          class="flex size-6 items-center justify-center rounded-full ring-offset-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          :class="[option.swatch, chosenColor === option.key && 'ring-2 ring-foreground/60']"
          @click="pickColor(option.key)"
        >
          <CheckIcon v-if="chosenColor === option.key" class="size-3.5 text-white" aria-hidden="true" />
        </button>
      </div>

      <label class="relative block">
        <span class="sr-only">Search icons</span>
        <MagnifyingGlassIcon class="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          v-model="search"
          type="search"
          placeholder="Search icons"
          data-testid="saved-view-icon-search"
          class="h-8 w-full rounded-lg border border-border bg-background pr-2 pl-8 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </label>

      <div role="radiogroup" aria-label="Icon" class="grid max-h-56 grid-cols-8 gap-1 overflow-y-auto">
        <button
          v-for="option in shownIcons"
          :key="option.key"
          type="button"
          role="radio"
          :aria-checked="chosenIcon === option.key"
          :aria-label="option.label"
          :title="option.label"
          :data-testid="`saved-view-icon-${option.key}`"
          class="flex size-8 items-center justify-center rounded-lg hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          :class="chosenIcon === option.key && 'bg-foreground/[0.08] ring-1 ring-border'"
          @click="pickIcon(option.key)"
        >
          <component :is="option.component" :class="['size-4.5', tint]" aria-hidden="true" />
        </button>
        <p v-if="shownIcons.length === 0" class="col-span-8 py-4 text-center text-sm text-muted-foreground">No icon matches "{{ search }}".</p>
      </div>

      <DialogFooter class="sm:justify-between">
        <Button variant="ghost" :disabled="isDefault" data-testid="saved-view-appearance-reset" @click="save(null, null)">
          Reset to bookmark
        </Button>
        <Button data-testid="saved-view-appearance-done" @click="open = false">Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
